import { useMemo, useState } from 'react';

import { useDailyStats, useWeeklyTier } from '@/features/agent-metrics/useAgentMetrics';
import { useCallAttempts } from '@/features/queue/callAttempts';
import { useBookings } from '@/features/bookings/useBookings';
import { useProfileSummary } from '@/features/agent-progress/progressHooks';
import { TIER_META } from '@/features/agent-progress/tierConstants';
import { useT, useLang, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';

import { Kicker } from '@/components/brand/brand';
import { Skeleton } from '@/components/chassis/States';
import { Meter, fmtHoursMinutes } from '@/components/agent/F5Bits';
import { CompletedListModal } from './CompletedListModal';

/**
 * F5-R8a · «Mis números» del agente (dibujo «Agente v2 — Header», pestaña
 * Mis números: «Tu día, tu mes y tu escalón.»). Los tiles MI DÍA y las
 * tarjetas MI MES que vivían apilados en QueuePage se MUDAN acá — misma
 * data, mismas derivaciones, mismos drill-downs (el tile «Enviados» sigue
 * abriendo la lista de completados). Cero lógica de negocio nueva.
 *
 * CERO PLATA: conteos, porcentajes y niveles; nunca montos (contrato F5-T7).
 */
export function NumbersPage(): JSX.Element {
  const t = useT();
  const lang = useLang();

  const dailyQ = useDailyStats();
  const tierQ = useWeeklyTier();
  const summaryQ = useProfileSummary();
  const attemptsQ = useCallAttempts(null, 200);
  const bookingsTodayQ = useBookings('today');

  const [completedOpen, setCompletedOpen] = useState(false);

  const submittedToday = dailyQ.data?.submittedToday ?? 0;

  // «Mi día» — derivaciones del dato vivo (MOVIDAS de QueuePage tal cual).
  const CONTACT_OUTCOMES = useMemo(
    () =>
      new Set([
        'refused',
        'language_barrier',
        'callback_requested',
        'client_ineligible',
        'already_screened_6mo',
      ]),
    [],
  );
  const dayStats = useMemo(() => {
    const rows = attemptsQ.data ?? [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const today = rows.filter((a) => new Date(a.attemptedAt) >= start);
    const contacts = today.filter((a) => CONTACT_OUTCOMES.has(a.outcome)).length;
    const seconds = today.reduce((acc, a) => acc + (a.durationSec ?? 0), 0);
    return { contacts, seconds };
  }, [attemptsQ.data, CONTACT_OUTCOMES]);

  // B-05: el denominador de intentos viene del COUNT del servidor, NO del largo
  // de la lista — esa lista está topada a 200 filas y congelaba el contador.
  const attemptsToday = dailyQ.data?.callAttemptsToday ?? 0;

  const apptsPendingCount = useMemo(() => {
    const rows = bookingsTodayQ.data ?? [];
    return rows.filter(
      (b) =>
        (b.status === 'scheduled' || b.status === 'rescheduled') &&
        new Date(b.scheduledAt).getTime() > Date.now() - 5 * 60_000,
    ).length;
  }, [bookingsTodayQ.data]);

  const monthly = summaryQ.data?.monthly ?? null;
  const monthName = monthly
    ? new Date(monthly.periodYear, monthly.periodMonth - 1, 1).toLocaleDateString(
        lang === 'es' ? 'es' : 'en',
        { month: 'long' },
      )
    : '';
  const weekTarget = tierQ.data?.thresholds.gold ?? 0;
  const weekCount = tierQ.data?.weekScreenings ?? 0;
  const weekPct = weekTarget > 0 ? Math.min(100, Math.round((weekCount / weekTarget) * 100)) : 0;

  return (
    <div className="max-w-[1080px] mx-auto font-body text-nav-navy">
      {/* Cabecera — pageCopy del dibujo. */}
      <header className="animate-fade-up-slow mb-4">
        <h1 className="font-head text-2xl font-extrabold tracking-[-0.02em] m-0">
          {t('agentF5.r8a.tabNumbers')}
        </h1>
        <p className="text-sm text-nav-slate mt-1 m-0">{t('agentF5.r8a.numbersSub')}</p>
      </header>

      {/* FRANJA «MI DÍA» — exactamente 4 tiles (MOVIDA de QueuePage). */}
      <section className="animate-fade-up-slow" style={{ animationDelay: '60ms' }}>
        <Kicker className="mt-[10px] mb-3.5">{t('agentF5.q.dayKicker')}</Kicker>
        <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(176px, 1fr))' }}>
          <DayTile
            value={attemptsQ.isLoading ? '—' : String(dayStats.contacts)}
            sub={attemptsQ.isLoading || dailyQ.isLoading ? undefined : `/ ${attemptsToday}`}
            label={t('agentF5.q.tileContacts')}
          />
          <DayTile
            value={dailyQ.isLoading ? '—' : String(submittedToday)}
            label={t('agentF5.q.tileSent')}
            onClick={() => setCompletedOpen(true)}
            ariaLabel={t('agentF5.q.tileSentAria')}
          />
          <DayTile
            value={attemptsQ.isLoading ? '—' : fmtHoursMinutes(dayStats.seconds)}
            label={t('agentF5.q.tileTime')}
          />
          <DayTile
            value={bookingsTodayQ.isLoading ? '—' : String(apptsPendingCount)}
            label={t('agentF5.q.tilePending')}
          />
        </div>
      </section>

      {/* FRANJA «MI MES» — nivel del mes + ritmo semanal, lado a lado
          A PROPÓSITO (MOVIDA de QueuePage). */}
      <section className="animate-fade-up-slow" style={{ animationDelay: '120ms' }}>
        <Kicker className="mt-[30px] mb-3.5">{t('agentF5.q.monthKicker')}</Kicker>
        <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))' }}>
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
            {summaryQ.isLoading || !monthly ? (
              <div className="space-y-3">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-2 w-full" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'w-4 h-4 rounded bg-gradient-to-br',
                      TIER_META[monthly.tierDisplayed].gradientClass,
                    )}
                  />
                  <p className="font-head text-lg font-extrabold m-0">
                    {t(TIER_META[monthly.tierDisplayed].i18nLabelKey as StringKey)}
                  </p>
                  <span className="ml-auto inline-block px-2.5 py-[3px] rounded-full bg-nav-hair border border-nav-card font-head text-[11px] font-bold text-nav-slate">
                    {monthName}
                  </span>
                </div>
                <p className="font-head text-3xl font-extrabold tabular-nums tracking-[-0.02em] mt-3 mb-0 leading-none">
                  {monthly.screeningsMonth}
                </p>
                <p className="text-[13px] text-nav-slate mt-1 m-0">{t('agentF5.q.monthCount')}</p>
                <Meter pct={monthly.progressPctToNextTier} tone="plata" className="mt-3" />
                <p className="text-[12.5px] text-nav-slate mt-2.5 m-0">
                  {monthly.nextTier && monthly.screeningsToNextTier != null
                    ? `${t('agentF5.q.monthNext', {
                        n: monthly.screeningsToNextTier,
                        tier: t(TIER_META[monthly.nextTier].i18nLabelKey as StringKey),
                      })} ${t('agentF5.q.monthLadder')}`
                    : `${t('agentF5.q.monthTop')} ${t('agentF5.q.monthLadder')}`}
                </p>
              </>
            )}
          </div>
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
            <p className="font-head text-[13.5px] font-bold m-0">{t('agentF5.q.weekTitle')}</p>
            <p className="font-head text-3xl font-extrabold tabular-nums tracking-[-0.02em] mt-3 mb-0 leading-none">
              {tierQ.isLoading ? '—' : weekCount}
            </p>
            <p className="text-[13px] text-nav-slate mt-1 m-0 tabular-nums">
              {tierQ.isLoading ? '' : t('agentF5.q.weekOf', { target: weekTarget, pct: weekPct })}
            </p>
            <Meter pct={weekPct} tone="teal" className="mt-3" />
            <p className="text-[12.5px] text-nav-slate mt-2.5 m-0">{t('agentF5.q.weekNote')}</p>
          </div>
        </div>
      </section>

      <CompletedListModal open={completedOpen} onClose={() => setCompletedOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Tile de «Mi día» (MOVIDO de QueuePage): hover -3px + borde teal si clickea. */
function DayTile({
  value,
  sub,
  label,
  onClick,
  ariaLabel,
}: {
  value: string;
  sub?: string;
  label: string;
  onClick?: () => void;
  ariaLabel?: string;
}): JSX.Element {
  const inner = (
    <>
      <p className="m-0 leading-none">
        <span className="font-head text-[30px] font-extrabold tabular-nums tracking-[-0.02em]">{value}</span>
        {sub ? <span className="font-head text-base font-bold text-nav-slate ml-1">{sub}</span> : null}
      </p>
      <p className="font-body text-[13px] text-nav-slate mt-3 m-0">{label}</p>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? label}
        className={cn(
          'text-left bg-white border-[1.5px] border-nav-card rounded-xl pt-[18px] px-5 pb-4 cursor-pointer',
          'transition-all duration-[180ms] hover:-translate-y-[3px] hover:border-nav-teal active:scale-[0.98]',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
        )}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="bg-white border-[1.5px] border-nav-card rounded-xl pt-[18px] px-5 pb-4">{inner}</div>
  );
}
